import React, { useState, useEffect } from 'react';
import { useDataProvider, useAuthState } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Container,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material';
import { PageTitle } from '../components/PageTitle';
import { Business as BusinessIcon, Email as EmailIcon, Phone as PhoneIcon } from '@mui/icons-material';

interface Brand {
  id: string;
  name: string;
}

interface Region {
  id: string;
  name: string;
}

interface Franchisee {
  id: string;
  code: string;
  name: string;
  franchiseeOwner?: string;
  franchiseeEmail?: string;
  franchiseePhone?: string;
  complianceScore?: number;
  royaltyRate?: number;
  brand?: Brand;
  region?: Region;
}

export const FranchiseesManagement = () => {
  const [enterpriseId, setEnterpriseId] = useState<string>('');
  const [brands, setBrands] = useState<Brand[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [franchisees, setFranchisees] = useState<Franchisee[]>([]);
  const [loading, setLoading] = useState(false);
  const [filteredLoading, setFilteredLoading] = useState(false);

  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [selectedRegionId, setSelectedRegionId] = useState<string>('');

  const dataProvider = useDataProvider();
  const { identity, isLoading: authLoading } = useAuthState();

  // Get enterprise ID from auth - fetch if needed
  useEffect(() => {
    const getEnterpriseId = async () => {
      try {
        // First try to get from identity
        if (identity) {
          const id = (identity as any)?.enterpriseId;
          if (id) {
            setEnterpriseId(id);
            return;
          }
        }

        // Try localStorage
        const stored = localStorage.getItem('user');
        if (stored) {
          const storedUser = JSON.parse(stored);
          if (storedUser?.enterpriseId) {
            setEnterpriseId(storedUser.enterpriseId);
            return;
          }
        }

        // If still no enterpriseId, fetch user's tenants to find enterprise
        // For enterprise admins, they should have access to enterprise resources
        try {
          const result = await dataProvider.getList('enterprises', {
            pagination: { page: 1, perPage: 1 },
            sort: { field: 'name', order: 'ASC' },
            filter: {},
          });

          if (result.data && result.data.length > 0) {
            setEnterpriseId(result.data[0].id);
          }
        } catch (error) {
          console.error('Error fetching enterprises:', error);
        }
      } catch (error) {
        console.error('Error getting enterprise ID:', error);
      }
    };

    if (!authLoading) {
      getEnterpriseId();
    }
  }, [identity, authLoading, dataProvider]);

  // Fetch brands for enterprise
  useEffect(() => {
    if (!enterpriseId) {
      console.log('No enterpriseId set, skipping brands fetch');
      return;
    }

    const fetchBrands = async () => {
      try {
        setLoading(true);

        console.log('Fetching brands for enterpriseId:', enterpriseId);

        // Fetch brands
        const brandsResult = await dataProvider.getList('brands', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { enterpriseId },
        });
        console.log('Brands result:', brandsResult);
        setBrands(brandsResult.data || []);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [enterpriseId, dataProvider]);

  // Fetch regions scoped to selected brand
  useEffect(() => {
    if (!enterpriseId) {
      console.log('No enterpriseId set, skipping regions fetch');
      setRegions([]);
      return;
    }

    // Clear selected region when brand changes
    setSelectedRegionId('');

    const fetchRegions = async () => {
      try {
        const filter: any = { enterpriseId };

        // If a brand is selected, only fetch regions for that brand
        if (selectedBrandId) {
          filter.brandId = selectedBrandId;
          console.log('Fetching regions for brandId:', selectedBrandId);
        } else {
          console.log('No brand selected, showing all regions for enterprise');
        }

        const regionsResult = await dataProvider.getList('regions', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter,
        });
        console.log('Regions result:', regionsResult);
        setRegions(regionsResult.data || []);
      } catch (error) {
        console.error('Error fetching regions:', error);
        setRegions([]);
      }
    };

    fetchRegions();
  }, [enterpriseId, selectedBrandId, dataProvider]);

  // Fetch franchisees with filters
  useEffect(() => {
    if (!enterpriseId) {
      console.log('No enterpriseId set, skipping franchisees fetch');
      return;
    }

    const fetchFranchisees = async () => {
      try {
        setFilteredLoading(true);
        const filter: any = { enterpriseId };

        if (selectedBrandId) {
          filter.brandId = selectedBrandId;
        }

        if (selectedRegionId) {
          filter.regionId = selectedRegionId;
        }

        console.log('Fetching franchisees with filter:', filter);

        const result = await dataProvider.getList('tenants', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'code', order: 'ASC' },
          filter,
        });

        console.log('Franchisees result:', result);
        setFranchisees(result.data || []);
      } catch (error) {
        console.error('Error fetching franchisees:', error);
        setFranchisees([]);
      } finally {
        setFilteredLoading(false);
      }
    };

    fetchFranchisees();
  }, [enterpriseId, selectedBrandId, selectedRegionId, dataProvider]);

  return (
    <Box>
      <PageTitle
        title="Franchise Management"
        subtitle="View and filter all franchisees across your enterprise"
        icon={<BusinessIcon />}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Use filters below to search for franchisees by brand or region. This view shows all
            franchisees (tenants) in your enterprise.
          </Typography>
        </Alert>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Brand</InputLabel>
                  <Select
                    value={selectedBrandId}
                    onChange={(e) => setSelectedBrandId(e.target.value)}
                    label="Brand"
                  >
                    <MenuItem value="">All Brands</MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth disabled={!selectedBrandId}>
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={selectedRegionId}
                    onChange={(e) => setSelectedRegionId(e.target.value)}
                    label="Region"
                  >
                    <MenuItem value="">
                      {selectedBrandId ? 'All Regions' : 'Select a brand first'}
                    </MenuItem>
                    {regions.map((region) => (
                      <MenuItem key={region.id} value={region.id}>
                        {region.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Franchisees Table */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Franchisees ({franchisees.length})
              </Typography>
              {filteredLoading && <CircularProgress size={24} />}
            </Box>

            {franchisees.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No franchisees found matching your filters
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#1C5195' }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1C5195' }}>Franchisee & Owner</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1C5195' }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1C5195' }}>Compliance</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1C5195' }}>Royalty Rate</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1C5195' }}>Brand</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1C5195' }}>Region</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {franchisees.map((franchisee, index) => {
                      const complianceScore = franchisee.complianceScore || 0;
                      let complianceColor = '#d32f2f'; // red
                      if (complianceScore >= 95) {
                        complianceColor = '#388e3c'; // green
                      } else if (complianceScore >= 85) {
                        complianceColor = '#f57c00'; // orange
                      }

                      return (
                        <TableRow
                          key={franchisee.id}
                          sx={{
                            backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff',
                            '&:hover': { backgroundColor: '#f0f0f0' },
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: '#0066CC' }}>
                            {franchisee.code}
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                                {franchisee.name || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#666' }}>
                                Owner: {franchisee.franchiseeOwner || '-'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {franchisee.franchiseeEmail && (
                                <Tooltip title={franchisee.franchiseeEmail}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}>
                                    <EmailIcon sx={{ fontSize: '0.9rem', color: '#666' }} />
                                    <span style={{ color: '#0066CC', cursor: 'pointer' }}>
                                      {franchisee.franchiseeEmail.substring(0, 20)}...
                                    </span>
                                  </Box>
                                </Tooltip>
                              )}
                              {franchisee.franchiseePhone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}>
                                  <PhoneIcon sx={{ fontSize: '0.9rem', color: '#666' }} />
                                  <span>{franchisee.franchiseePhone}</span>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${complianceScore.toFixed(1)}%`}
                              sx={{
                                backgroundColor: complianceColor,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {((franchisee.royaltyRate || 0) * 100).toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={franchisee.brand?.name || '-'}
                              variant="outlined"
                              size="small"
                              sx={{ borderColor: '#1C5195', color: '#1C5195', fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{franchisee.region?.name || '-'}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};
